/**
 * Iter 226 — Reminders Intelligence Report (admin/owner only).
 * Aggregates reminder_events over the last N days.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  BellRing, TrendingDown, Flame, MoonStar, UserPlus,
  CheckCircle2, Timer, Sliders,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

export default function RemindersIntelReport() {
  const { activeWorkspaceId } = useAuth();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [threshold, setThreshold] = useState(3);
  const [savingThreshold, setSavingThreshold] = useState(false);

  const load = () => {
    if (!activeWorkspaceId) return;
    setLoading(true); setError(null);
    api.get(`/workspaces/${activeWorkspaceId}/reminders/reports/intelligence?days=${days}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/settings/reminders`)
      .then(({ data }) => setThreshold(data?.hot_potato_threshold || 3))
      .catch(() => {});
    load();
     
  }, [activeWorkspaceId, days]);

  const saveThreshold = async () => {
    setSavingThreshold(true);
    try {
      await api.put(`/workspaces/${activeWorkspaceId}/settings/reminders`, { hot_potato_threshold: Number(threshold) });
      toast.success("Threshold saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSavingThreshold(false); }
  };

  const t = data?.totals || {};

  return (
    <>
      <PageHeader
        eyebrow="Reminders"
        title="Intelligence Report"
        description="Owner & admin visibility — how are reminders performing across the team?"
        testid="reminders-intel-page"
        actions={
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="text-xs px-2 py-1.5 bg-background border border-border"
              data-testid="reminders-intel-days"
            >
              {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 space-y-6">
        {error && (
          <div className="border border-rose-500/40 bg-rose-500/5 text-rose-700 text-sm p-4">
            {error === "Owner/admin only" ? "This report is available to workspace owners and admins only." : error}
          </div>
        )}
        {!error && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <KPI icon={BellRing} title="Reminders seen" value={t.reminders_seen ?? "—"} />
              <KPI icon={MoonStar} title="Snoozes" value={t.snoozes ?? 0} tone={t.snoozes > 20 ? "amber" : "slate"} />
              <KPI icon={UserPlus} title="Delegations" value={t.delegations ?? 0} />
              <KPI icon={CheckCircle2} title="Completed" value={t.completed ?? 0} tone="emerald" />
              <KPI icon={Flame} title="Hot potatoes" value={t.hot_potato_count ?? 0} tone={t.hot_potato_count > 0 ? "rose" : "slate"} sub={`≥ ${t.hot_potato_threshold} passes`} />
              <KPI icon={Timer} title="Avg. time to complete" value={t.avg_hours_to_complete != null ? `${t.avg_hours_to_complete}h` : "—"} />
              <KPI icon={TrendingDown} title="Events logged" value={t.events_logged ?? 0} />
            </div>

            <div className="border border-border p-4 max-w-xl" data-testid="reminders-intel-threshold">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                <Sliders className="w-3.5 h-3.5" /> Hot-potato threshold
              </div>
              <p className="text-xs text-muted-foreground mb-2">Reminders passed to a new owner this many times or more are flagged.</p>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1} max={20}
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  className="w-20 text-xs px-2 py-1.5 bg-background border border-border"
                  data-testid="reminders-intel-threshold-input"
                />
                <button
                  type="button"
                  onClick={saveThreshold}
                  disabled={savingThreshold}
                  className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                  data-testid="reminders-intel-threshold-save"
                >
                  {savingThreshold ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Panel title="Hot potato reminders" icon={Flame} testid="reminders-intel-hot-potato">
                {(data?.hot_potato || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">None right now. 🎉</div>
                ) : (
                  <div className="divide-y divide-border">
                    {data.hot_potato.slice(0, 10).map((r) => (
                      <div key={r.reminder_id} className="py-2 text-xs" data-testid={`hot-potato-${r.reminder_id}`}>
                        <div className="font-medium">{r.title || r.reminder_id}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          <span className="text-rose-600 font-semibold">{r.delegation_hop_count} passes</span>
                          · currently with <b>{r.current_owner}</b>
                          · {r.snooze_count} snoozes · last activity {fmtRelative(r.last_at)}
                        </div>
                        {r.delegation_chain?.length > 0 && (
                          <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                            Chain:
                            {r.delegation_chain.map((h, i) => (
                              <span key={i} className="inline-flex items-center gap-1">
                                <span className="font-mono">{h.from || "—"}</span>
                                <span>→</span>
                                <span className="font-mono">{h.to || "—"}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Top snoozers" icon={MoonStar} testid="reminders-intel-top-snoozers">
                {(data?.top_snoozers || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No snoozes in this window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {data.top_snoozers.map((s) => (
                      <div key={s.user_id} className="flex items-center gap-2 text-xs" data-testid={`top-snoozer-${s.user_id}`}>
                        <div className="flex-1 truncate">{s.name}</div>
                        <div className="w-32 h-1.5 bg-muted overflow-hidden">
                          <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (s.snoozes / (data.top_snoozers[0]?.snoozes || 1)) * 100)}%` }} />
                        </div>
                        <div className="w-10 text-right font-mono">{s.snoozes}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Top delegators" icon={UserPlus} testid="reminders-intel-top-delegators">
                {(data?.top_delegators || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No delegations in this window.</div>
                ) : (
                  <div className="space-y-1.5">
                    {data.top_delegators.map((s) => (
                      <div key={s.user_id} className="flex items-center gap-2 text-xs" data-testid={`top-delegator-${s.user_id}`}>
                        <div className="flex-1 truncate">{s.name}</div>
                        <div className="w-32 h-1.5 bg-muted overflow-hidden">
                          <div className="h-full bg-sky-500" style={{ width: `${Math.min(100, (s.delegations / (data.top_delegators[0]?.delegations || 1)) * 100)}%` }} />
                        </div>
                        <div className="w-10 text-right font-mono">{s.delegations}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Most-nagged reminders" icon={BellRing} testid="reminders-intel-per-reminder" className="lg:col-span-2">
                {(data?.per_reminder || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">{loading ? "Loading…" : "No activity yet."}</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-muted-foreground border-b border-border">
                        <th className="py-1.5 pr-2">Reminder</th>
                        <th className="py-1.5 pr-2">Current owner</th>
                        <th className="py-1.5 pr-2 text-right">Shown</th>
                        <th className="py-1.5 pr-2 text-right">Snoozes</th>
                        <th className="py-1.5 pr-2 text-right">Passes</th>
                        <th className="py-1.5 pr-2 text-right">Completed</th>
                        <th className="py-1.5 pr-2">Last activity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.per_reminder.slice(0, 20).map((r) => (
                        <tr key={r.reminder_id} className="border-b border-border/60" data-testid={`per-reminder-${r.reminder_id}`}>
                          <td className="py-1.5 pr-2 truncate max-w-[280px]" title={r.title}>{r.title || r.reminder_id}</td>
                          <td className="py-1.5 pr-2 truncate max-w-[160px]">{r.current_owner}</td>
                          <td className="py-1.5 pr-2 text-right font-mono">{r.shown_count}</td>
                          <td className="py-1.5 pr-2 text-right font-mono">{r.snooze_count}</td>
                          <td className={`py-1.5 pr-2 text-right font-mono ${r.delegation_hop_count >= threshold ? "text-rose-600 font-semibold" : ""}`}>{r.delegation_hop_count}</td>
                          <td className="py-1.5 pr-2 text-right font-mono">{r.completed_count}</td>
                          <td className="py-1.5 pr-2 text-muted-foreground">{fmtRelative(r.last_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function KPI({ icon: Icon, title, value, sub, tone = "slate" }) {
  const map = {
    slate: "border-border",
    emerald: "border-emerald-500/50 bg-emerald-500/5",
    amber: "border-amber-500/50 bg-amber-500/5",
    rose: "border-rose-500/50 bg-rose-500/5",
  };
  return (
    <div className={`border ${map[tone]} p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="w-3 h-3" />} {title}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Panel({ title, icon: Icon, className = "", children, testid }) {
  return (
    <div className={`border border-border p-4 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      {children}
    </div>
  );
}
